import { forwardRef, useMemo } from 'react';
import {
  RadioGroup as AriaRadioGroup,
  Radio as AriaRadio,
  composeRenderProps,
} from 'react-aria-components';
import { useController } from 'react-hook-form';
import { Label } from './label';

export const UncontrolledRadioGroup = forwardRef(function UncontrolledRadioGroup(
  { label, layout = 'flex', options, Component = RadioCard, ...props },
  ref,
) {
  const radioOptions = useMemo(
    () =>
      options.map((option) => (
        <Component key={option.value} value={option.value} data-value={option.value}>
          {option.label}
        </Component>
      )),
    [options],
  );
  return (
    <AriaRadioGroup
      {...props}
      className="group/radio flex flex-col gap-1"
      ref={ref}
      data-name={props.name}
      data-size={props.size ?? ''}
      data-layout={layout}
    >
      <Label>{label}</Label>
      <div className="flex group-data-[orientation=vertical]/radio:flex-col group-data-[orientation=horizontal]/radio:flex-row group-data-[layout=grid]/radio:grid group-data-[layout=grid]/radio:grid-cols-3 sm:group-data-[layout=grid]/radio:grid-cols-5 group-data-[layout=grid]/radio:gap-4">
        {radioOptions}
      </div>
    </AriaRadioGroup>
  );
});

export function RadioGroup({ onInteraction, ...props }) {
  const { field } = useController({ name: props.name });
  const onChange = (...args) => {
    onInteraction?.();
    field.onChange(...args);
  };
  return (
    <UncontrolledRadioGroup {...props} {...field} onChange={onChange} isDisabled={props.disabled} />
  );
}

export function RadioItem(props) {
  return (
    <AriaRadio
      {...props}
      role="radio"
      className="group px-3 py-4 flex relative gap-x-3 cursor-pointer items-center text-neutral-800 disabled:text-neutral-300 font-medium -mt-[1px] border-y border-solid last:border-b-0 border-t-transparent data-[selected]:bg-primary-50 data-[selected]:border-y-primary-light"
    >
      {composeRenderProps(props.children, (children) => (
        <>
          <div className="size-4.5 box-border rounded-full border border-gray-300 bg-white dark:bg-neutral-900 transition-all group-data-[hovered=true]:border-primary group-data-[selected=true]:border-primary group-data-[selected=true]:border-[0.33rem]" />
          {children}
        </>
      ))}
    </AriaRadio>
  );
}

export function RadioCard(props) {
  return (
    <AriaRadio
      {...props}
      role="radio"
      className="group p-2 min-w-24 text-base group-data-[size=lg]/radio:text-lg group-data-[size=lg]/radio:py-4 group-data-[size=lg]/radio:px-2 group-data-[size=lg]/radio:min-w-30 group-data-[layout=flex]/radio:group-data-[orientation=vertical]/radio:mb-4 group-data-[layout=flex]/radio:group-data-[orientation=horizontal]/radio:mr-4 last:mb-0 last:mr-0 flex relative gap-x-3 cursor-pointer items-center text-neutral-800 disabled:text-neutral-300 rounded-md border border-solid border-300 data-[hovered=true]:border-primary data-[selected=true]:bg-primary-50 data-[selected]:border-primary data-[focused]:ring-1 ring-primary"
    >
      {composeRenderProps(props.children, (children) => (
        <>
          <div className="size-4.5 box-border rounded-full border border-gray-300 bg-white dark:bg-neutral-900 transition-all group-data-[hovered=true]:border-primary group-data-[selected=true]:border-primary group-data-[selected=true]:border-[0.33rem]" />
          <div>{children}</div>
        </>
      ))}
    </AriaRadio>
  );
}
